import { qualityAllowed } from 'video-quality';

export function holdGalleryFilm(connection, reducedMotion) {
  return reducedMotion || connection?.saveData === true || /^(slow-2g|2g|3g)$/.test(connection?.effectiveType || '') || (connection?.downlink > 0 && connection.downlink < 2);
}

export function selectGalleryFilm(sources, { width, height, dpr = 1, zoom = 1, maxHeight = 1080, canPlayType = () => true }) {
  const valid = (Array.isArray(sources) ? sources : []).filter(source => source?.url && /^video\/mp4(?:;|$)/i.test(source.type || '') && source.width > 0 && source.height > 0 && canPlayType(source.type)).sort((a, b) => a.width * a.height - b.width * b.height);
  const candidates = valid.filter(source => source.height <= maxHeight);
  if (!candidates.length) return valid[0];
  const density = Math.max(1, Math.min(2, dpr)) * Math.max(1, Math.min(1.25, Number(zoom) || 1));
  return candidates.find(source => source.width >= width * density * 0.98 && source.height >= height * density * 0.98) || candidates.at(-1);
}

export async function selectOptimizedGalleryFilm(sources, geometry, decodingInfo) {
  const eligible = source => {
    if (source.minViewportWidth && geometry.width < source.minViewportWidth) return false;
    const needsQuality = source.tier === 'quality' || (source.qualityBelowWidth > 0 && geometry.width < source.qualityBelowWidth);
    return !needsQuality || qualityAllowed(source, geometry.connection);
  };
  const candidates = ['hevc', 'av1', 'h264']
    .map(codec => selectGalleryFilm(sources.filter(source => source.codec === codec && eligible(source)), { ...geometry, maxHeight: 1440 }))
    .filter(Boolean)
    .sort((a, b) => Number(b.preferred === true) - Number(a.preferred === true));
  const assessments = candidates.map(async source => {
    if (source.codec === 'h264') return source;
    if (!decodingInfo || !(source.bitrate > 0) || !(source.framerate > 0)) return null;
    let timeout;
    try {
      const info = await Promise.race([
        decodingInfo({ type: source.transport === 'hls' ? 'media-source' : 'file', video: { contentType: source.type, width: source.width, height: source.height, bitrate: source.bitrate, framerate: source.framerate } }),
        new Promise(resolve => { timeout = setTimeout(() => resolve(null), 800); }),
      ]);
      return info?.supported && info.smooth && info.powerEfficient ? source : null;
    } catch { return null; } finally { clearTimeout(timeout); }
  });
  for (const assessment of assessments) {
    const source = await assessment;
    if (source && eligible(source)) return source;
  }
  return null;
}

export class GalleryFilm extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.video = this.querySelector('video');
    this.poster = this.querySelector('img');
    this.button = this.querySelector('button');
    this.soundButton = this.querySelector('[data-film-sound]');
    if (!this.video) return;
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.connection = navigator.connection;
    this.inView = false;
    this.manual = false;
    this.userPaused = false;
    this.failed = false;
    this.fallbackUsed = false;
    this.optimizedFailed = false;
    this.optimizedSource = null;
    this.generation = (this.generation || 0) + 1;
    this.video.muted = true;
    this.video.controls = false;
    this.video.tabIndex = -1;
    this.soundButton?.setAttribute('hidden', '');
    this.soundButton?.setAttribute('aria-pressed', 'false');
    this.sources = this.readSources();
    if (this.sources.length) {
      this.soundButton?.removeAttribute('hidden');
      this.soundButton?.addEventListener('click', () => this.toggleSound(), { signal });
    }
    if (!this.sources.length) return;
    this.button?.removeAttribute('hidden');
    this.button?.addEventListener('click', () => this.toggle(), { signal });
    const preferenceChanged = () => {
      this.manual = false;
      if (holdGalleryFilm(this.connection, this.motion.matches)) this.unload();
      this.update();
    };
    this.motion.addEventListener('change', preferenceChanged, { signal });
    this.connection?.addEventListener('change', preferenceChanged, { signal });
    document.addEventListener('visibilitychange', () => this.update(), { signal });
    this.video.addEventListener('playing', () => {
      clearTimeout(this.stallTimer);
      this.stallTimer = null;
      this.syncControl();
      if (this.video.requestVideoFrameCallback) {
        this.frameCallback = this.video.requestVideoFrameCallback(() => this.reveal());
      } else this.reveal();
    }, { signal });
    this.video.addEventListener('pause', () => {
      this.syncControl();
    }, { signal });
    this.video.addEventListener('volumechange', () => this.soundButton?.setAttribute('aria-pressed', String(!this.video.muted)), { signal });
    this.video.addEventListener('waiting', () => this.watchStall(), { signal });
    this.video.addEventListener('stalled', () => this.watchStall(), { signal });
    this.video.addEventListener('error', () => this.recover(), { signal });
    this.observer = new IntersectionObserver(entries => {
      this.inView = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.05);
      this.update();
    }, { threshold: [0, 0.05] });
    this.observer.observe(this);
    this.syncControl();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    this.unload();
    this.abort = null;
  }

  readSources() {
    let supplied, native;
    try { supplied = JSON.parse(this.dataset.renditions || '{}')?.[this.dataset.videoId]; } catch {}
    try { native = JSON.parse(this.dataset.sources || '[]'); } catch { native = []; }
    const usable = sources => (Array.isArray(sources) ? sources : []).filter(source => source?.url && source.width > 0 && source.height > 0 && (source.type?.toLowerCase().includes('mpegurl') || (/^video\/mp4(?:;|$)/i.test(source.type || '') && this.video.canPlayType(source.type))));
    const selected = usable(supplied);
    const nativeSources = usable(native);
    return selected.length ? [...selected, ...nativeSources.filter(source => !selected.some(candidate => candidate.url === source.url))] : nativeSources;
  }

  eligible() {
    return this.isConnected && this.inView && !document.hidden;
  }

  update() {
    if (!this.eligible() || this.userPaused || this.failed || (!this.manual && holdGalleryFilm(this.connection, this.motion.matches))) {
      this.suspend();
      return;
    }
    this.start();
  }

  toggle() {
    if (!this.video.paused || this.starting) {
      this.userPaused = true;
      this.suspend();
    } else {
      this.manual = true;
      this.userPaused = false;
      this.failed = false;
      this.start();
    }
  }

  toggleSound() {
    if (!this.sources.length) return;
    const enable = this.video.muted;
    this.video.muted = !enable;
    this.soundButton?.setAttribute('aria-pressed', String(enable));
    if (enable) {
      this.manual = true;
      this.userPaused = false;
      this.failed = false;
      this.start();
    }
  }

  async start() {
    if (!this.eligible() || this.starting || !this.video.paused) return;
    this.starting = true;
    const generation = this.generation;
    if (!this.video.getAttribute('src')) {
      let posterDeadline;
      try {
        await Promise.race([this.poster?.decode(), new Promise(resolve => { posterDeadline = setTimeout(resolve, 2000); })]);
      } catch {} finally { clearTimeout(posterDeadline); }
      if (!this.manual) await new Promise(resolve => {
        if (window.requestIdleCallback) window.requestIdleCallback(resolve, { timeout: 800 });
        else requestAnimationFrame(resolve);
      });
      if (generation !== this.generation || !this.eligible()) {
        if (generation === this.generation) this.starting = false;
        return;
      }
      const rect = this.getBoundingClientRect();
      this.choice = selectGalleryFilm(this.sources.filter(source => !source.codec), { width: rect.width, height: rect.height, dpr: devicePixelRatio, zoom: this.dataset.zoom, canPlayType: type => this.video.canPlayType(type) });
      if (!await this.attachSource(generation)) {
        if (generation === this.generation) this.starting = false;
        return;
      }
    }
    if (generation !== this.generation || !this.eligible()) return;
    this.resumeStream();
    this.watchStall();
    try {
      await this.video.play();
    } catch (error) {
      if (generation === this.generation && error.name !== 'AbortError') this.stop();
    } finally {
      if (generation === this.generation) this.starting = false;
    }
  }

  loadHls() {
    return import(this.dataset.hlsModule).then(module => module.default);
  }

  async attachSource(generation) {
    if (!this.optimizedFailed && this.sources.some(source => source.codec)) {
      const rect = this.getBoundingClientRect();
      const source = await selectOptimizedGalleryFilm(this.sources, { width: rect.width, height: rect.height, dpr: devicePixelRatio, zoom: this.dataset.zoom, connection: this.connection, canPlayType: type => this.video.canPlayType(type) }, navigator.mediaCapabilities?.decodingInfo?.bind(navigator.mediaCapabilities));
      if (generation !== this.generation || !this.eligible()) return false;
      if (source) {
        if (source.transport === 'hls') {
          if (await this.attachStream(source, generation, 'optimized')) return true;
          if (generation !== this.generation || !this.eligible()) return false;
          this.optimizedFailed = true;
        } else {
          this.mode = 'optimized';
          this.optimizedSource = source;
          this.video.width = source.width;
          this.video.height = source.height;
          this.video.src = source.url;
          this.video.load();
          return true;
        }
      }
    }
    const playlist = !this.fallbackUsed && this.sources.find(source => source.type?.toLowerCase().includes('mpegurl'));
    const nativeHls = playlist && this.video.canPlayType(playlist.type);
    if (nativeHls && navigator.vendor === 'Apple Computer, Inc.') {
      this.mode = 'native-hls';
      this.video.src = playlist.url;
      this.video.load();
      return true;
    }
    if (playlist && await this.attachStream(playlist, generation)) return true;
    if (generation !== this.generation || !this.eligible()) return false;
    if (nativeHls) {
      this.mode = 'native-hls';
      this.video.src = playlist.url;
      this.video.load();
      return true;
    }
    if (!this.choice) { this.stop(); return false; }
    this.mode = 'mp4';
    this.video.width = this.choice.width;
    this.video.height = this.choice.height;
    this.video.src = this.choice.url;
    this.video.load();
    return true;
  }

  async attachStream(playlist, generation, mode = 'hls') {
    if (!this.dataset.hlsModule) return false;
    let hls;
    try {
      const Hls = await this.loadHls();
      if (generation !== this.generation || !this.eligible() || !Hls.isSupported()) return false;
      if (mode === 'optimized' && !Hls.getMediaSource?.()?.isTypeSupported(playlist.type)) return false;
      hls = this.hls = new Hls({
        autoStartLoad: false,
        maxBufferLength: 3,
        maxMaxBufferLength: 6,
        maxBufferSize: 1024 * 1024,
        backBufferLength: 3,
        lowLatencyMode: false,
        startFragPrefetch: false,
      });
      this.mode = mode;
      this.optimizedSource = mode === 'optimized' ? playlist : null;
      this.playlist = playlist;
      this.streamSuspended = false;
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (this.hls !== hls) return;
        this.capStream(true);
        if (this.eligible() && !this.userPaused && (this.manual || !holdGalleryFilm(this.connection, this.motion.matches))) hls.startLoad(this.video.currentTime || 0);
      });
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (this.hls !== hls || data.details === Hls.ErrorDetails.INTERNAL_ABORTED) return;
        const networkFailure = this.mode === 'optimized' && data.type === Hls.ErrorTypes.NETWORK_ERROR
          && !this.streamSuspended && this.eligible() && !this.userPaused;
        if (data.fatal || networkFailure) this.recover();
      });
      this.resizeObserver = new ResizeObserver(() => this.capStream());
      this.resizeObserver.observe(this);
      hls.attachMedia(this.video);
      hls.loadSource(playlist.url);
      return true;
    } catch {
      if (hls && this.hls === hls) {
        this.hls = null;
        this.optimizedSource = null;
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        hls.destroy();
      }
      return false;
    }
  }

  resumeStream() {
    if (!this.hls) return;
    this.hls.resumeBuffering();
    if (this.hls.levels.length) this.hls.startLoad(this.video.currentTime || 0);
    else if (this.streamSuspended) this.hls.loadSource(this.playlist.url);
    this.streamSuspended = false;
  }

  capStream(initial = false) {
    if (!this.hls?.levels.length) return;
    const rect = this.getBoundingClientRect();
    const levels = this.hls.levels.map((level, index) => ({ ...level, url: String(index), type: 'video/mp4' }));
    const cap = selectGalleryFilm(levels, { width: rect.width, height: rect.height, dpr: devicePixelRatio, zoom: this.dataset.zoom, maxHeight: this.mode === 'optimized' ? 1440 : 1080 });
    if (!cap) return;
    this.hls.autoLevelCapping = Number(cap.url);
    // Display size limits useful quality; the adaptive player estimates which
    // level the connection can sustain instead of starting at that ceiling.
    if (initial) this.hls.startLevel = -1;
  }

  recover() {
    if (this.mode === 'optimized' && !this.optimizedFailed) {
      this.optimizedFailed = true;
      this.unload();
      this.update();
    } else if (!this.fallbackUsed && (this.mode === 'hls' || this.mode === 'native-hls') && this.choice) {
      this.fallbackUsed = true;
      this.unload();
      this.update();
    } else this.stop();
  }

  reveal() {
    if (this.eligible() && !this.video.paused && this.video.readyState >= 2) this.setAttribute('has-frame', '');
  }

  watchStall() {
    if (this.stallTimer || !this.eligible()) return;
    this.stallTimer = setTimeout(() => {
      this.stallTimer = null;
      if (this.eligible() && this.video.readyState < 3) this.stop();
    }, 5000);
  }

  stop() {
    this.failed = true;
    this.unload();
  }

  suspend() {
    this.generation = (this.generation || 0) + 1;
    this.starting = false;
    clearTimeout(this.stallTimer);
    this.stallTimer = null;
    this.video?.cancelVideoFrameCallback?.(this.frameCallback);
    if (this.hls) this.streamSuspended = true;
    this.hls?.pauseBuffering();
    this.hls?.stopLoad();
    this.video?.pause();
    this.syncControl();
  }

  unload() {
    this.suspend();
    const hls = this.hls;
    this.hls = null;
    this.optimizedSource = null;
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    hls?.destroy();
    if (this.video?.getAttribute('src')) {
      this.video.removeAttribute('src');
      this.video.load();
    }
    this.removeAttribute('has-frame');
  }

  syncControl() {
    const playing = this.video && !this.video.paused;
    this.toggleAttribute('data-playing', playing);
    this.toggleAttribute('data-play-required', !playing && (this.failed || (!this.manual && holdGalleryFilm(this.connection, this.motion.matches))));
    this.button?.setAttribute('aria-label', playing ? this.dataset.pauseLabel : this.dataset.playLabel);
  }
}

if (!customElements.get('gallery-film')) customElements.define('gallery-film', GalleryFilm);
